<script setup>
import BaseStatusChip from '@/components/base/BaseStatusChip.vue'
import BaseDeleteConfirm from '@/components/base/BaseDeleteConfirm.vue'
import ProductPriceFormDialog from '@/components/products-page/ProductPriceFormDialog.vue'

import { computed, defineEmits, defineProps } from 'vue'
import { useStore } from '@/utils/VuexHelpers'
import { formatCurrency, formatDate } from '@/helpers/stringHelpers'
import { isActivePrice, isFuturePrice } from '@/utils/ProductPriceHelpers'

const props = defineProps({
  prices: {
    type: Array,
    default: () => []
  },
  customer: {
    type: Object,
    default: null
  },
  product: {
    type: Object,
    default: null
  },
  allowProductSelect: {
    type: Boolean,
    default: false
  },
  allowCustomerSelect: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['refresh'])

const store = useStore()
const loadingProdPrice = computed(() => store.getters.loadingProdPrice)
const productNames = computed(() => store.getters.productNames || [])
const customerNames = computed(() => store.getters.customerNames || [])
const loadingProducts = computed(() => store.getters.loadingProducts)
const loadingCustomers = computed(() => store.getters.loadingCustomers)

const headerClass = 'blue-grey darken-1 white--text'
const pricingHeaders = computed(() => {
  const baseHeaders = []

  if (props.product) {
    baseHeaders.push(
      {
        text: 'Customer Code',
        value: 'customer_code',
        align: 'start',
        width: '160',
        sortable: true,
        class: headerClass
      },
      {
        text: 'Customer',
        value: 'customer_name',
        align: 'start',
        sortable: true,
        class: headerClass
      },
      {
        text: 'Product Code',
        value: 'product_code',
        align: 'end',
        width: '160',
        sortable: true,
        class: headerClass
      }
    )
  } else {
    baseHeaders.push(
      {
        text: 'Code',
        value: 'product_code',
        align: 'end',
        width: '100',
        sortable: true,
        class: headerClass
      },
      {
        text: 'Product',
        value: 'product_name',
        sortable: true,
        class: headerClass
      },
      {
        text: 'Standard Price',
        value: 'default_price',
        align: 'end',
        sortable: false,
        class: headerClass
      },
      {
        text: 'Customer',
        value: 'customer_code',
        sortable: false,
        align: 'start',
        class: headerClass
      }
    )
  }

  return [
    ...baseHeaders,
    {
      text: 'Price/Kg',
      value: 'price_kg',
      align: 'end',
      class: headerClass
    },
    {
      text: 'Start Date',
      value: 'start_date',
      align: 'center',
      sortable: true,
      class: headerClass
    },
    {
      text: 'End Date',
      value: 'end_date',
      align: 'center',
      class: headerClass
    },
    {
      text: 'Status',
      value: 'status',
      class: headerClass
    },
    {
      text: 'Comment',
      value: 'comment',
      class: headerClass
    },
    {
      text: 'Actions',
      value: 'actions',
      sortable: false,
      class: headerClass
    }
  ]
})

const pricesWithCustomer = computed(() => {
  return props.prices.filter((price) => {
    return price.customer_id !== null && price.customer_id !== undefined
  })
})

const productMap = computed(() => {
  return new Map(
    productNames.value.map((product) => {
      return [Number(product.id), product]
    })
  )
})

const customerMap = computed(() => {
  return new Map(
    customerNames.value.map((customer) => {
      return [Number(customer.id), customer]
    })
  )
})

const enrichedPrices = computed(() => {
  const prices = pricesWithCustomer.value

  if (props.customer) {
    return prices.map((price) => {
      const product = productMap.value.get(Number(price.productlist_id))

      return {
        ...price,
        product,
        product_code: product?.code || '',
        product_name: product?.product || '',
        default_price: product?.default_price || '-',
        customer_code: props.customer?.code || '',
        customer_name: props.customer?.company?.name || ''
      }
    })
  }

  if (props.product) {
    const product = props.product
    return prices.map((price) => {
      const customer = customerMap.value.get(Number(price.customer_id))

      return {
        ...price,
        customer,
        product_code: product?.code || '',
        product_name: product?.product || '',
        customer_code: customer?.code || '',
        customer_name: customer?.name || ''
      }
    })
  }

  return prices
})

const deletePrice = async (price) => {
  const payload = {
    id: price.id,
    product: props.product || null
  }

  const res = await store.dispatch('deleteProdPrice', { payload })

  if (res !== 'error') {
    emit('refresh')
  }
}

// ON CREATED
if (
  productNames.value.length === 0 &&
  loadingProducts.value !== 'names' &&
  props.customer
) {
  store.dispatch('fetchProductNames')
}
if (
  customerNames.value.length === 0 &&
  loadingCustomers.value !== 'names' &&
  props.product
) {
  store.dispatch('fetchCustomerNames')
}
</script>

<template>
  <div>
    <div class="d-flex justify-start mb-2">
      <ProductPriceFormDialog
        :customer="customer"
        :product="product"
        :allow-product-select="allowProductSelect"
        :allow-customer-select="allowCustomerSelect"
        @saved="emit('refresh')"
      />
    </div>

    <v-data-table
      :headers="pricingHeaders"
      :items="enrichedPrices"
      sort-by="product_code"
      :sort-desc="false"
      dense
      hide-default-footer
      disable-pagination
      class="nested-pricing-table"
    >
      <template #item.price_kg="{ value }">
        {{ formatCurrency(value) }}
      </template>

      <template #item.default_price="{ value }">
        {{ formatCurrency(value) }}
      </template>

      <template #item.start_date="{ value }">
        {{ formatDate(value) }}
      </template>

      <template #item.end_date="{ value }">
        {{ formatDate(value) }}
      </template>

      <template #item.status="{ item: price }">
        <BaseStatusChip
          v-if="isFuturePrice(price)"
          label="Future"
          color="blue darken-1"
        />

        <BaseStatusChip
          v-else-if="isActivePrice(price)"
          label="Active"
          color="green darken-1"
        />

        <BaseStatusChip v-else label="Expired" color="grey darken-1" />
      </template>

      <template #item.actions="{ item: price }">
        <v-row class="text-nowrap align-center flex-nowrap" no-gutters>
          <ProductPriceFormDialog
            :customer="customer ? customer : price.customer"
            :product="product ? product : price.product"
            :edit="true"
            :price="price"
            @saved="emit('refresh')"
          />

          <BaseDeleteConfirm
            :icon="true"
            size="small"
            title="Are you sure you want to delete this product price?"
            :loading="loadingProdPrice === `delete-${price.id}`"
            @deleteConfirm="() => deletePrice(price)"
          >
            <template v-slot:content>
              <div class="text-subtitle-2 mt-2">Customer:</div>
              <div class="font-weight-bold">
                {{ price.customer_name }}
              </div>

              <div class="text-subtitle-2 mt-2">Product:</div>
              <div class="font-weight-bold">
                {{ price.product_name }}
              </div>

              <div class="text-subtitle-2 mt-2">Price/Kg:</div>
              <div class="font-weight-bold">
                {{ formatCurrency(price.price_kg) }}
              </div>

              <div class="text-subtitle-2 mt-2">Effective Date:</div>
              <div class="font-weight-bold">
                {{ formatDate(price.start_date) }}
              </div>
            </template>
          </BaseDeleteConfirm>
        </v-row>
      </template>
    </v-data-table>
  </div>
</template>

<style scoped>
.nested-pricing-table {
  background: #fafafa !important;
}
</style>
